import { queryOptions, useQuery } from "@tanstack/react-query";

import data from "../../assets/mocks/market-performance.json";
import { getDataFilteredByRange } from "../portfolio/utils";
import { mapPayloadToMarketPerfomance } from "./utils";

import { env } from "@/config/env";
import { api } from "@/lib/api-client";
import { hasAccessToken } from "@/lib/auth";
import { AxiosMock } from "@/utils/mock";

import type { QueryConfig } from "@/lib/react-query";
import type { DatesRange } from "@/types/entity";
import type { PerformancePayload } from "@/types/market";
import type { Performance } from "@/types/performance";

type CarPerformanceParams = Partial<DatesRange> & {
  carId?: string;
};

if (import.meta.env.MODE === "test") {
  // Mocking the API response for watchlists
  const mock = AxiosMock.getInstance();
  const uri = new RegExp("/carburate/market/cars/.+/valuation/");
  mock.onGet(uri).reply(200, data);
}

export const getMarketCarValuation = async ({ carId, from, to }: CarPerformanceParams) => {
  const query = from && to ? `?from=${from}&to=${to}` : "";
  const response = (await api.get(
    `/carburate/market/cars/${carId}/valuation/${query}`
  )) as PerformancePayload;
  return mapPayloadToMarketPerfomance(response);
};

export const getMarketCarValuationQueryOptions = ({ carId, from, to }: CarPerformanceParams) => {
  return queryOptions({
    queryKey: ["market", "cars", carId, "valuation", { from, to }],
    queryFn: () => getMarketCarValuation({ carId, from, to }),
  });
};

type UseMarketCarValuationOptions = CarPerformanceParams & {
  queryConfig?: QueryConfig<typeof getMarketCarValuation>;
};

export const useMarketCarValuation = ({
  carId,
  from,
  queryConfig,
  to,
}: UseMarketCarValuationOptions) => {
  const isClientPagination = env.PAGINATION === "client";

  const currentFrom = isClientPagination ? undefined : from;
  const currentTo = isClientPagination ? undefined : to;

  let data: Performance;

  const {
    data: response,
    isSuccess,
    ...props
  } = useQuery({
    enabled: hasAccessToken() && !!carId,
    ...getMarketCarValuationQueryOptions({ carId, from: currentFrom, to: currentTo }),
    ...queryConfig,
  });

  data = response!;
  if (isClientPagination && isSuccess) {
    data = getDataFilteredByRange(data, from && to ? { from, to } : undefined);
  }

  return { data, isSuccess, ...props };
};
